/*
 * MUIのコンポーネントに適用されるHermes Consoleのテーマ定義
 */
import { createTheme } from "@mui/material/styles";
import { colors } from "./colors";
import { fontSizes } from "./fonts";

// TODO: why does displayPrint property not work??
export const theme = createTheme({
    typography: {
        fontFamily: "Noto Sans JP",
        fontSize: fontSizes.normal, // px
        htmlFontSize: 16 / 0.875, // html base px / 14px to rem
        h1: {
            fontSize: fontSizes.xxlarge,
            fontWeight: "bold",
        },
        h2: {
            fontSize: fontSizes.xlarge,
            fontWeight: "bold",
        },
        h3: {
            fontSize: fontSizes.large,
            fontWeight: "bold",
        },
        h4: {
            fontSize: fontSizes.normal,
            fontWeight: "bold",
        },
        h5: {
            fontSize: fontSizes.small,
            fontWeight: "bold",
        },
        h6: {
            fontSize: fontSizes.small,
        },
        body1: {
            fontSize: fontSizes.normal,
        },
    },
    breakpoints: {
        values: {}, // no breakpoints (only desktop app)
    },
    palette: {
        primary: {
            main: colors.green.main,
        },
        secondary: {
            main: colors.black.main,
        },
        info: {
            main: colors.blue.main,
        },
        error: {
            main: colors.red.main,
        },
        common: {
            black: colors.black.main,
            white: colors.white.main,
        },
        text: {
            primary: colors.black.main,
            secondary: colors.gray.main,
            disabled: colors.gray.f50,
        },
        action: {
            active: colors.black.main,
            selected: colors.green.f90,
            hover: colors.gray.a10,
        },
        background: {
            default: colors.gray.f90,
            paper: colors.white.main,
        },
        divider: colors.gray.a20,
    },
    shape: {
        borderRadius: 4,
    },
    spacing: 4, // use sx) p: 1 = padding: 4px, m: 2 = margin: 8px
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                root: {
                    import:
                        "url('https://fonts.googleapis.com/css2?family=Noto+Sans+JP:wght@400;700;800&display=swap')",
                },
                body: {
                    "&, header, main": {
                        minWidth: 800, // 800pxを最小幅としている（それ以上は横スクロール）
                    },
                    // Date Picker
                    ".MuiCalendarPicker-root": {
                        ".MuiTypography-root": {
                            fontSize: fontSizes.normal,
                        },
                        ".MuiTypography-caption": {
                            color: colors.gray.main,
                        },
                        ".MuiButtonBase-root": {
                            fontSize: fontSizes.normal,
                            svg: {
                                color: colors.green.main,
                            },
                        },
                        ".PrivatePickersFadeTransitionGroup-root": {
                            fontWeight: "bold",
                        },
                        ".MuiPickersDay-root": {
                            width: "40px",
                            "&:hover": {
                                backgroundColor: colors.gray.a10,
                            },
                            "&:focus": {
                                ".Mui-selected": {
                                    color: colors.white.main,
                                    backgroundColor: colors.green.main,
                                },
                            },
                            "&.Mui-selected": {
                                fontWeight: "bold",
                                color: colors.white.main,
                                backgroundColor: colors.green.main,
                                "&:hover": {
                                    backgroundColor: colors.green.b20,
                                },
                            },
                            "&.MuiPickersDay-today:not(.Mui-selected)": {
                                color: colors.green.main,
                                borderColor: colors.green.main,
                            },
                        },
                    },
                },
            },
        },
        MuiBackdrop: {
            styleOverrides: {
                root: {
                    backgroundColor: colors.transparent.main,
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    fontSize: fontSizes.normal,
                    lineHeight: "28px",
                    fontWeight: "bold",
                    "[disabled]": {
                        color: colors.gray.a50,
                    },
                },
                sizeSmall: {
                    fontSize: fontSizes.small,
                },
                sizeLarge: {
                    fontSize: fontSizes.large,
                },
                // Primary
                containedPrimary: {
                    ":hover": {
                        backgroundColor: colors.green.b20,
                    },
                    "[disabled]": {
                        borderColor: colors.gray.a20,
                    },
                },
                // Secondly
                outlinedPrimary: {
                    borderColor: colors.gray.a20,
                    ":hover": {
                        backgroundColor: colors.gray.a10,
                        borderColor: colors.gray.a20,
                    },
                    "[disabled]": {
                        borderColor: colors.gray.a20,
                    },
                },
                // Thirdly
                textPrimary: {
                    borderColor: colors.gray.a20,
                    height: "40px",
                    ":hover": {
                        backgroundColor: colors.gray.a10,
                    },
                },
                // White
                containedSecondary: {
                    backgroundColor: colors.white.main,
                    color: colors.green.main,
                    ":hover": {
                        backgroundColor: colors.gray.f90,
                    },
                    "[disabled]": {
                        borderColor: colors.gray.a20,
                    },
                },
                // Cancel
                textSecondary: {
                    color: colors.gray.main,
                    borderColor: colors.gray.a20,
                    height: "40px",
                    ":hover": {
                        backgroundColor: colors.gray.a10,
                    },
                },
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    color: colors.black.main,
                },
            },
        },
        MuiLink: {
            styleOverrides: {
                root: {
                    textDecoration: "none",
                    ":hover": {
                        opacity: "0.8",
                    },
                },
            },
        },
        MuiListItem: {
            styleOverrides: {
                root: {
                    paddingLeft: 8,
                    paddingRight: 8,
                },
            },
        },
        MuiListItemButton: {
            styleOverrides: {
                root: {
                    borderRadius: 4,
                    "&:hover": {
                        backgroundColor: colors.gray.a10,
                    },
                    "&.Mui-selected": {
                        "& .MuiTypography-root": {
                            fontWeight: "bold",
                            color: colors.white.main,
                        },
                        "&, &:hover": {
                            backgroundColor: colors.green.main,
                        },
                    },
                },
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    "&.Mui-selected": {
                        fontWeight: "bold",
                        color: colors.green.main,
                    },
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    boxShadow: "none",
                },
            },
        },
        MuiSnackbar: {
            styleOverrides: {
                root: {
                    left: 32,
                    bottom: 32,
                    width: 344,
                },
            },
        },
        MuiSnackbarContent: {
            styleOverrides: {
                root: {
                    fontSize: fontSizes.normal,
                    backgroundColor: colors.gray.b90,
                },
            },
        },
        MuiTableRow: {
            styleOverrides: {
                root: {
                    ":hover": {
                        boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.2)",
                    },
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    fontSize: fontSizes.normal,
                },
            },
        },
        MuiAlert: {
            styleOverrides: {
                root: {
                    fontSize: fontSizes.normal,
                    "&.MuiAlert-outlinedError .MuiAlert-icon": {
                        color: colors.red.main,
                    },
                    "&.MuiAlert-outlinedInfo .MuiAlert-icon": {
                        color: colors.blue.main,
                    },
                },
                outlinedError: {
                    backgroundColor: colors.red.f90,
                },
                outlinedInfo: {
                    backgroundColor: colors.blue.f90,
                },
            },
        },
        MuiDialog: {
            styleOverrides: {
                root: {
                    "& .MuiDialog-paper": {
                        width: 320,
                    },
                },
            },
        },
        MuiDialogTitle: {
            styleOverrides: {
                root: {
                    fontSize: fontSizes.normal,
                    fontWeight: "bold",
                },
            },
        },
        MuiFilledInput: {
            styleOverrides: {
                root: {
                    backgroundColor: colors.gray.a10,
                },
            },
        },
        MuiAutocomplete: {
            styleOverrides: {
                root: {
                    "& .MuiFilledInput-root.MuiInputBase-sizeSmall": {
                        paddingBottom: 4,
                    },
                },
            },
        },
        MuiSelect: {
            styleOverrides: {
                filled: {
                    paddingTop: 22,
                    paddingBottom: 6,
                },
            },
        },
    },
});
